import React, {useMemo, useState} from 'react';
import {Alert, FlexColumn, FlexRow, Modal, NumberField} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ExpenseRow} from '../ExpenseTypes';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import BulkUpdateExpenseItemsMutation from '../../../mutations/bulk_update_expense_items_mutation';
import Util from '../../../forecast-app/shared/util/util';
import {partitionArray} from '../../../forecast-app/shared/util/array-utils';
import {FieldLabel} from './ModalComponents';

type ExpenseManagementModalProps = {
	selectedRows: ExpenseRow[];
	closeModal: () => void;
};

const ChangeExpensesMarkupModal = ({selectedRows, closeModal}: ExpenseManagementModalProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const initialMarkupValue = selectedRows.length === 1 && selectedRows[0].markup ? selectedRows[0].markup * 100 : 0;

	const [markup, setMarkup] = useState(initialMarkupValue);

	const [lockedRows, unlockedRows] = useMemo(() => partitionArray(selectedRows, item => item.locked), [selectedRows]);

	const unlockedAndBillableRows = useMemo(() => unlockedRows.filter(row => row.billable), unlockedRows);

	const numLockedExpenses = lockedRows.length;
	const numTotalExpenses = selectedRows.length;

	const onUpdate = () => {
		closeModal();
		if (unlockedAndBillableRows.length < 1) return;
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: formatMessage({
					id:
						unlockedAndBillableRows.length > 1
							? 'expense_management.has_been_updated_bulk'
							: 'expense_management.has_been_updated',
				}),
			});
		};
		Util.CommitMutation(
			BulkUpdateExpenseItemsMutation,
			{
				ids: unlockedAndBillableRows.map(({id}) => id),
				markup: markup / 100,
			},
			onSuccess
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="expense_management.modal.adjust_markup" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<FieldLabel
						helperTextId="expense_management.modal.adjust_markup_only_billable"
						labelId="expense_management.modal.markup"
					>
						<NumberField value={markup} onChange={setMarkup}></NumberField>
					</FieldLabel>
					{lockedRows.length ? (
						selectedRows.length > 1 ? (
							<FlexRow>
								<Alert
									text={formatMessage(
										{id: 'expense_management.change_markup.locked_alert'},
										{numLockedExpenses, numTotalExpenses}
									)}
									type={Alert.TYPE.INFORMATION}
								/>
							</FlexRow>
						) : (
							<FlexRow>
								<Alert
									text={formatMessage({id: 'expense_management.modal.expense_locked_cannot_be_changed'})}
									type={Alert.TYPE.INFORMATION}
								/>
							</FlexRow>
						)
					) : null}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={onUpdate}>
					<FormattedMessage id="expense_management.actions.confirm_changes" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ChangeExpensesMarkupModal;
